import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    useTheme,
    useMediaQuery,
    Typography
} from '@mui/material';
import CommonCard from '../../../../components/card/CommonCard';
import ProgressBar from '../../../../components/charts/progressBar';
import ChartSection from '../../../../components/charts/chartSection';
import Toggle from '../../../../components/toggle';
import WeeklyChart from '../../../../components/charts/barChart/weeklyChart';
import ReportCard from '../../../reports/components/reportCard';
import { formatDateTime, formatDateTimeDisplay } from '../../../../utils/base';
import { CHALLENGE_HEARTBEAT_MAX } from '../../../../constants';

export const HeartRateTab = ({ physicalRecords }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [activeView, setActiveView] = useState(0); // 0: 일간, 1: 주간, 2: 월간, 3: 연간
    // 주간/월간/연간 인덱스 상태 관리
    const [currentWeekIndex, setCurrentWeekIndex] = useState(0);
    const [currentMonthIndex, setCurrentMonthIndex] = useState(0);
    const [currentYearIndex, setCurrentYearIndex] = useState(0);
    const [selectedBarIndex, setSelectedBarIndex] = useState(-1);

    // 주간 데이터 생성 (실제 데이터 사용)
    const generateThisWeek = () => {
        const today = new Date();
        const currentDay = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

        // Get start of week (Sunday)
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - currentDay);

        const dayNames = [t('sun'), t('mon'), t('tue'), t('wed'), t('thu'), t('fri'), t('sat')];
        const week = [];

        for (let i = 0; i < 7; i++) {
            const date = new Date(startOfWeek);
            date.setDate(startOfWeek.getDate() + i);

            const isSelected = date.getTime() === selectedDate.getTime();

            // 해당 날짜의 실제 심박수 데이터 찾기
            const dateStr = date.toISOString().split('T')[0];
            const dayRecords = physicalRecords?.filter(record =>
                record.date && record.date.startsWith(dateStr)
            );

            // 심박수 데이터 집계
            let heartRateData = null;
            if (dayRecords && dayRecords.length > 0) {
                const validRecords = dayRecords.filter(record =>
                    record.minHeartRate != null || record.maxHeartRate != null || record.avgHeartRate != null
                );

                if (validRecords.length > 0) {
                    heartRateData = validRecords.reduce((acc, record) => {
                        if (acc === null) {
                            // 첫 번째 레코드
                            return {
                                min: record.minHeartRate ?? null,
                                max: record.maxHeartRate ?? null,
                                avg: record.avgHeartRate ?? null
                            };
                        }

                        const min = record.minHeartRate != null
                            ? (acc.min != null ? Math.min(acc.min, record.minHeartRate) : record.minHeartRate)
                            : acc.min;
                        const max = record.maxHeartRate != null
                            ? (acc.max != null ? Math.max(acc.max, record.maxHeartRate) : record.maxHeartRate)
                            : acc.max;
                        const avg = record.avgHeartRate != null
                            ? (acc.avg != null ? Math.round((acc.avg + record.avgHeartRate) / 2) : record.avgHeartRate)
                            : acc.avg;

                        return { min, max, avg };
                    }, null);
                }
            }

            week.push({
                day: dayNames[i],
                date: date,
                isSelected: isSelected,
                heartRateData: heartRateData,
                hasData: dayRecords && dayRecords.length > 0
            });
        }

        return week;
    };

    const days = useMemo(() => generateThisWeek(), [selectedDate, physicalRecords]);

    // 선택된 시간대 상태
    const [selectedHourIndex, setSelectedHourIndex] = useState(-1); // 기본값: 선택 안됨

    const handleDayClick = (date) => {
        setSelectedDate(date);
    };

    // 선택된 바를 해제하는 함수
    const clearSelectedBar = () => {
        setSelectedBarIndex(-1);
    };

    // 바 선택 핸들러 (시간 선택도 리셋)
    const handleBarSelect = (index) => {
        setSelectedBarIndex(index);
        setSelectedHourIndex(-1); // 시간 선택 리셋 (선택 안됨)
    };

    // 주간 심박수 데이터 (1년치 고정)
    const allWeeklyData = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const weeklyData = [];

        const earliestDate = new Date(today);
        earliestDate.setDate(today.getDate() - 364);
        earliestDate.setHours(0, 0, 0, 0);
        const totalDays = 365;

        for (let i = 0; i < totalDays; i++) {
            const date = new Date(earliestDate);
            date.setDate(earliestDate.getDate() + i);

            // 로컬 시간 기준으로 날짜 문자열 생성 (시간대 문제 방지)
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const dateStr = `${year}-${month}-${day}`;
            const dayRecords = physicalRecords?.filter(record =>
                record.date && record.date.startsWith(dateStr)
            );

            // 해당 날짜의 심박수 데이터 집계
            let heartRateData = null;
            if (dayRecords && dayRecords.length > 0) {
                const validRecords = dayRecords.filter(record =>
                    record.minHeartRate != null || record.maxHeartRate != null || record.avgHeartRate != null
                );

                if (validRecords.length > 0) {
                    heartRateData = validRecords.reduce((acc, record) => {
                        if (acc === null) {
                            // 첫 번째 레코드
                            return {
                                min: record.minHeartRate ?? null,
                                max: record.maxHeartRate ?? null,
                                avg: record.avgHeartRate ?? null
                            };
                        }

                        const min = record.minHeartRate != null
                            ? (acc.min != null ? Math.min(acc.min, record.minHeartRate) : record.minHeartRate)
                            : acc.min;
                        const max = record.maxHeartRate != null
                            ? (acc.max != null ? Math.max(acc.max, record.maxHeartRate) : record.maxHeartRate)
                            : acc.max;
                        const avg = record.avgHeartRate != null
                            ? (acc.avg != null ? Math.round((acc.avg + record.avgHeartRate) / 2) : record.avgHeartRate)
                            : acc.avg;

                        return { min, max, avg };
                    }, null);
                }
            }

            weeklyData.push({
                day: dateStr,
                min: heartRateData?.min,
                max: heartRateData?.max,
                avg: heartRateData?.avg,
                isCurrent: date.toDateString() === today.toDateString(), // 오늘이 현재 날짜
                color: theme.palette.figma.heartbeat,
                hasData: dayRecords && dayRecords.length > 0
            });
        }

        return weeklyData;
    }, [physicalRecords, theme.palette.figma.heartbeat]);

    // 현재 선택된 날짜의 데이터
    const currentDayData = useMemo(() => {
        // 먼저 days 배열에서 찾기 (현재 주 데이터)
        const selectedDateData = days.find(day => {
            const dayDate = new Date(day.date);
            dayDate.setHours(0, 0, 0, 0);
            const selectedDateNormalized = new Date(selectedDate);
            selectedDateNormalized.setHours(0, 0, 0, 0);
            return dayDate.getTime() === selectedDateNormalized.getTime();
        });
        if (selectedDateData?.heartRateData) {
            return selectedDateData.heartRateData;
        }

        // days에서 찾지 못하면 allWeeklyData에서 찾기 (과거 데이터 포함)
        // 로컬 시간 기준으로 날짜 문자열 생성 (시간대 문제 방지)
        const selectedDateNormalized = new Date(selectedDate);
        selectedDateNormalized.setHours(0, 0, 0, 0);
        const year = selectedDateNormalized.getFullYear();
        const month = String(selectedDateNormalized.getMonth() + 1).padStart(2, '0');
        const day = String(selectedDateNormalized.getDate()).padStart(2, '0');
        const selectedDateStr = `${year}-${month}-${day}`;

        const weeklyDataItem = allWeeklyData.find(item => item.day === selectedDateStr);
        if (weeklyDataItem && (weeklyDataItem.min != null || weeklyDataItem.max != null || weeklyDataItem.avg != null)) {
            return {
                min: weeklyDataItem.min,
                max: weeklyDataItem.max,
                avg: weeklyDataItem.avg
            };
        }

        // 둘 다 없으면 null 반환
        return null;
    }, [days, selectedDate, allWeeklyData]);

    const handleScrollToEnd = () => { };

    // 월간 데이터 생성
    const monthlyData = useMemo(() => {
        const today = new Date();
        const targetMonth = new Date(today);
        targetMonth.setMonth(today.getMonth() + currentMonthIndex);

        const year = targetMonth.getFullYear();
        const month = targetMonth.getMonth() + 1;
        const daysInMonth = new Date(year, month, 0).getDate();

        const monthData = [];

        for (let day = 1; day <= daysInMonth; day++) {
            const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const dayRecords = physicalRecords?.filter(record =>
                record.date && record.date.startsWith(dateStr)
            );

            let heartRateData = null;
            if (dayRecords && dayRecords.length > 0) {
                const validRecords = dayRecords.filter(record =>
                    record.minHeartRate != null || record.maxHeartRate != null || record.avgHeartRate != null
                );

                if (validRecords.length > 0) {
                    heartRateData = validRecords.reduce((acc, record) => {
                        if (acc === null) {
                            return {
                                min: record.minHeartRate ?? null,
                                max: record.maxHeartRate ?? null,
                                avg: record.avgHeartRate ?? null
                            };
                        }

                        const min = record.minHeartRate != null
                            ? (acc.min != null ? Math.min(acc.min, record.minHeartRate) : record.minHeartRate)
                            : acc.min;
                        const max = record.maxHeartRate != null
                            ? (acc.max != null ? Math.max(acc.max, record.maxHeartRate) : record.maxHeartRate)
                            : acc.max;
                        const avg = record.avgHeartRate != null
                            ? (acc.avg != null ? Math.round((acc.avg + record.avgHeartRate) / 2) : record.avgHeartRate)
                            : acc.avg;

                        return { min, max, avg };
                    }, null);
                }
            }

            monthData.push({
                day: dateStr,
                min: heartRateData?.min || 0,
                max: heartRateData?.max || 0,
                avg: heartRateData?.avg || 0,
                isCurrent: day === today.getDate() && month === today.getMonth() + 1 && year === today.getFullYear(),
                color: theme.palette.figma.heartbeat,
                hasData: dayRecords && dayRecords.length > 0
            });
        }

        return monthData;
    }, [physicalRecords, currentMonthIndex, theme.palette.figma.heartbeat]);

    // 연간 데이터 생성
    const yearlyData = useMemo(() => {
        const today = new Date();
        const targetYear = today.getFullYear() + currentYearIndex;

        const yearData = [];

        for (let month = 1; month <= 12; month++) {
            const monthStr = `${targetYear}-${String(month).padStart(2, '0')}`;
            const monthRecords = physicalRecords?.filter(record => {
                const recordDate = new Date(record.date);
                return recordDate.getFullYear() === targetYear && recordDate.getMonth() + 1 === month;
            });

            let monthlyMin = 0;
            let monthlyMax = 0;

            if (monthRecords && monthRecords.length > 0) {
                const validRecords = monthRecords.filter(record =>
                    record.minHeartRate != null || record.maxHeartRate != null
                );

                if (validRecords.length > 0) {
                    const minValues = validRecords.map(record => record.minHeartRate || 0).filter(val => val > 0);
                    const maxValues = validRecords.map(record => record.maxHeartRate || 0).filter(val => val > 0);
                    monthlyMin = minValues.length > 0 ? Math.min(...minValues) : 0;
                    monthlyMax = maxValues.length > 0 ? Math.max(...maxValues) : 0;
                }
            }

            yearData.push({
                day: monthStr,
                min: monthlyMin,
                max: monthlyMax,
                isCurrent: month === today.getMonth() + 1 && targetYear === today.getFullYear(),
                color: theme.palette.figma.heartbeat,
                hasData: monthRecords && monthRecords.length > 0
            });
        }

        return yearData;
    }, [physicalRecords, currentYearIndex, theme.palette.figma.heartbeat]);

    // 현재 뷰에 맞는 데이터 선택
    const currentViewData = useMemo(() => {
        switch (activeView) {
            case 0: // 일간
                return allWeeklyData;
            case 1: // 주간
                return allWeeklyData;
            case 2: // 월간
                return monthlyData;
            case 3: // 연간
                return yearlyData;
            default:
                return allWeeklyData;
        }
    }, [activeView, allWeeklyData, monthlyData, yearlyData]);

    // 현재 뷰에 맞는 통계 계산
    const currentViewStats = useMemo(() => {
        const data = currentViewData;
        if (!data || data.length === 0) {
            return { min: 0, max: 0, avg: 0 };
        }

        const validMinValues = data.filter(item => item.min > 0).map(item => item.min);
        const validMaxValues = data.filter(item => item.max > 0).map(item => item.max);
        const validAvgValues = data.filter(item => item.avg > 0).map(item => item.avg);

        return {
            min: validMinValues.length > 0 ? Math.min(...validMinValues) : 0,
            max: validMaxValues.length > 0 ? Math.max(...validMaxValues) : 0,
            avg: validAvgValues.length > 0 ? Math.round(validAvgValues.reduce((a, b) => a + b, 0) / validAvgValues.length) : 0
        };
    }, [currentViewData]);

    // 주간 데이터 생성 (heartbeatWeek.js 로직)
    const getCurrentWeekData = useMemo(() => {
        const today = new Date();
        const weekData = [];

        // 현재 주의 월요일부터 일요일까지 7일 데이터 생성
        const startOfWeek = new Date(today);
        const dayOfWeek = today.getDay();
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        startOfWeek.setDate(today.getDate() + mondayOffset + (currentWeekIndex * 7));

        for (let i = 0; i < 7; i++) {
            const date = new Date(startOfWeek);
            date.setDate(startOfWeek.getDate() + i);
            const dateStr = formatDateTime(date, '-', ':', 0, 10);

            // 실제 데이터에서 해당 날짜 찾기
            const actualRecord = physicalRecords?.find(record => {
                const recordDate = formatDateTime(new Date(record.date), '-', ':', 0, 10);
                return recordDate === dateStr;
            });

            const minHeartRate = actualRecord?.minHeartRate || 0;
            const maxHeartRate = actualRecord?.maxHeartRate || 0;

            weekData.push({
                day: dateStr,
                min: minHeartRate,
                max: maxHeartRate,
                color: theme.palette.figma.heartRate
            });
        }

        return weekData;
    }, [physicalRecords, currentWeekIndex, theme.palette.figma.heartRate]);

    // 주간 통계 계산
    const calculateCurrentWeekStats = useMemo(() => {
        const currentData = getCurrentWeekData;

        if (currentData.length === 0) {
            return {
                minHeartRate: 0,
                maxHeartRate: 0,
            };
        }

        const validMinValues = currentData.filter(item => item.min > 0).map(item => item.min);
        const validMaxValues = currentData.filter(item => item.max > 0).map(item => item.max);

        const minHeartRate = validMinValues.length > 0 ? Math.min(...validMinValues) : 0;
        const maxHeartRate = validMaxValues.length > 0 ? Math.max(...validMaxValues) : 0;

        return {
            minHeartRate: minHeartRate,
            maxHeartRate: maxHeartRate,
        };
    }, [getCurrentWeekData]);

    // 주간 제목 생성 (다국어 지원)
    const getWeeklyTitle = useMemo(() => {
        const currentWeekData = getCurrentWeekData;
        if (currentWeekData.length === 0) return t('no_weekly_data');

        const firstDay = new Date(currentWeekData[0].day);
        const lastDay = new Date(currentWeekData[currentWeekData.length - 1].day);

        const startFormatted = formatDateTimeDisplay(firstDay, 'monthDay');
        const endFormatted = formatDateTimeDisplay(lastDay, 'monthDay');
        return `${startFormatted} - ${endFormatted}`;
    }, [getCurrentWeekData, t]);

    // 월간 데이터 생성 (heartbeatMonth.js 로직)
    const getCurrentMonthData = useMemo(() => {
        const today = new Date();
        const targetMonth = new Date(today);
        targetMonth.setMonth(today.getMonth() + currentMonthIndex);

        const year = targetMonth.getFullYear();
        const month = targetMonth.getMonth() + 1;
        const daysInMonth = new Date(year, month, 0).getDate();

        const monthData = [];

        for (let day = 1; day <= daysInMonth; day++) {
            const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

            // 실제 데이터에서 해당 날짜 찾기
            const actualRecord = physicalRecords?.find(record => {
                const recordDate = formatDateTime(new Date(record.date), '-', ':', 0, 10);
                return recordDate === dateStr;
            });

            const minHeartRate = actualRecord?.minHeartRate || 0;
            const maxHeartRate = actualRecord?.maxHeartRate || 0;

            monthData.push({
                day: dateStr,
                min: minHeartRate,
                max: maxHeartRate,
                color: theme.palette.figma.heartRate
            });
        }

        return monthData;
    }, [physicalRecords, currentMonthIndex, theme.palette.figma.heartRate]);

    // 월간 통계 계산
    const calculateCurrentMonthStats = useMemo(() => {
        const currentData = getCurrentMonthData;

        if (currentData.length === 0) {
            return {
                minHeartRate: 0,
                maxHeartRate: 0,
            };
        }

        const validMinValues = currentData.filter(item => item.min > 0).map(item => item.min);
        const validMaxValues = currentData.filter(item => item.max > 0).map(item => item.max);

        const minHeartRate = validMinValues.length > 0 ? Math.min(...validMinValues) : 0;
        const maxHeartRate = validMaxValues.length > 0 ? Math.max(...validMaxValues) : 0;

        return {
            minHeartRate: minHeartRate,
            maxHeartRate: maxHeartRate,
        };
    }, [getCurrentMonthData]);

    // 월간 제목 생성 (다국어 지원)
    const getMonthlyTitle = useMemo(() => {
        const currentMonthData = getCurrentMonthData;
        if (currentMonthData.length === 0) return t('no_monthly_data');

        const firstDay = currentMonthData[0].day;
        const firstDate = new Date(firstDay);

        return formatDateTimeDisplay(firstDate, 'yearMonth');
    }, [getCurrentMonthData, t]);

    // 현재 월의 일수 계산
    const getCurrentMonthDays = useMemo(() => {
        const currentMonthData = getCurrentMonthData;
        if (currentMonthData.length === 0) return 31;

        const firstDay = currentMonthData[0].day;
        const firstDate = new Date(firstDay);
        const year = firstDate.getFullYear();
        const month = firstDate.getMonth() + 1;

        const lastDay = new Date(year, month, 0).getDate();
        return lastDay;
    }, [getCurrentMonthData]);

    // 연간 데이터 생성 (heartbeatYear.js 로직)
    const getCurrentYearData = useMemo(() => {
        const today = new Date();
        const targetYear = today.getFullYear() + currentYearIndex;

        const yearData = [];

        for (let month = 1; month <= 12; month++) {
            const monthStr = `${targetYear}-${String(month).padStart(2, '0')}`;

            // 해당 월의 모든 데이터 필터링
            const monthRecords = physicalRecords?.filter(record => {
                const recordDate = new Date(record.date);
                return recordDate.getFullYear() === targetYear && recordDate.getMonth() + 1 === month;
            }) || [];

            // 월별 최소/최대값 계산
            let monthlyMin = 0;
            let monthlyMax = 0;

            if (monthRecords.length > 0) {
                const minValues = monthRecords.map(record => record.minHeartRate || 0).filter(val => val > 0);
                monthlyMin = minValues.length > 0 ? Math.min(...minValues) : 0;

                const maxValues = monthRecords.map(record => record.maxHeartRate || 0).filter(val => val > 0);
                monthlyMax = maxValues.length > 0 ? Math.max(...maxValues) : 0;
            }

            yearData.push({
                day: monthStr,
                min: monthlyMin,
                max: monthlyMax,
                color: theme.palette.figma.heartRate
            });
        }

        return yearData;
    }, [physicalRecords, currentYearIndex, theme.palette.figma.heartRate]);

    // 연간 통계 계산
    const calculateCurrentYearStats = useMemo(() => {
        const currentData = getCurrentYearData;

        if (currentData.length === 0) {
            return {
                minHeartRate: 0,
                maxHeartRate: 0,
            };
        }

        const validMinValues = currentData.filter(item => item.min > 0).map(item => item.min);
        const validMaxValues = currentData.filter(item => item.max > 0).map(item => item.max);

        const minHeartRate = validMinValues.length > 0 ? Math.min(...validMinValues) : 0;
        const maxHeartRate = validMaxValues.length > 0 ? Math.max(...validMaxValues) : 0;

        return {
            minHeartRate: minHeartRate,
            maxHeartRate: maxHeartRate,
        };
    }, [getCurrentYearData]);

    // 연간 제목 생성 (다국어 지원)
    const getYearlyTitle = useMemo(() => {
        const currentYearData = getCurrentYearData;
        if (currentYearData.length === 0) return t('no_yearly_data');

        const firstDay = currentYearData[0].day;
        const firstDate = new Date(firstDay + '-01');

        return formatDateTimeDisplay(firstDate, 'year');
    }, [getCurrentYearData, t]);

    // 선택된 날짜 계산 (뷰에 따라 다름)
    const selectedDateForHourly = useMemo(() => {
        if (activeView === 0) {
            // 일간: selectedDate 사용
            return selectedDate;
        } else if (activeView === 1) {
            // 주간: 선택된 바의 날짜
            if (selectedBarIndex >= 0 && selectedBarIndex < getCurrentWeekData.length) {
                const selectedDay = getCurrentWeekData[selectedBarIndex].day;
                return new Date(selectedDay);
            }
            // 선택되지 않았으면 주간의 첫 번째 날짜
            if (getCurrentWeekData.length > 0) {
                return new Date(getCurrentWeekData[0].day);
            }
            return selectedDate;
        } else if (activeView === 2) {
            // 월간: 선택된 바의 날짜
            if (selectedBarIndex >= 0 && selectedBarIndex < getCurrentMonthData.length) {
                const selectedDay = getCurrentMonthData[selectedBarIndex].day;
                return new Date(selectedDay);
            }
            // 선택되지 않았으면 오늘 날짜
            return new Date();
        } else if (activeView === 3) {
            // 연간: 선택된 바의 월의 첫 번째 날짜
            if (selectedBarIndex >= 0 && selectedBarIndex < getCurrentYearData.length) {
                const selectedMonth = getCurrentYearData[selectedBarIndex].day;
                return new Date(selectedMonth + '-01');
            }
            // 선택되지 않았으면 현재 월의 첫 번째 날짜
            return new Date();
        }
        return selectedDate;
    }, [activeView, selectedDate, selectedBarIndex, getCurrentWeekData, getCurrentMonthData, getCurrentYearData]);

    // 시간별 심박수 데이터 (hourlyHeartRates 사용)
    const hourlyHeartbeatData = useMemo(() => {
        if (!physicalRecords || physicalRecords.length === 0) {
            return new Array(24).fill([0, 0]); // 기본값
        }

        const selectedDateStr = selectedDateForHourly.toISOString().split('T')[0];
        const selectedRecord = physicalRecords?.find(record =>
            record.date && record.date.startsWith(selectedDateStr)
        );

        // 기본값으로 24시간 데이터 생성 [min, max]
        const defaultValue = [0, 0];
        const defaultData = Array(24).fill(defaultValue);

        if (selectedRecord && selectedRecord.hourlyHeartRates) {
            try {
                // hourlyHeartRates가 JSON 문자열인 경우 파싱
                let hourlyHeartRates = selectedRecord.hourlyHeartRates;
                if (typeof hourlyHeartRates === 'string') {
                    hourlyHeartRates = JSON.parse(hourlyHeartRates);
                }

                if (Array.isArray(hourlyHeartRates) && hourlyHeartRates.length >= 24) {
                    return hourlyHeartRates.slice(0, 24).map(hourData => {
                        if (Array.isArray(hourData) && hourData.length >= 2) {
                            const [min, max] = hourData;
                            // 데이터가 유효한 경우에만 사용
                            if (min > 0 && max > 0) {
                                return [min, max];
                            }
                        }
                        return defaultValue; // 기본값
                    });
                }
            } catch (e) {
                console.error('시간별 심박수 데이터 파싱 실패:', e);
            }
        }

        return defaultData;
    }, [physicalRecords, selectedDateForHourly]);

    // 선택된 시간대 데이터 계산
    const selectedHourData = selectedHourIndex >= 0 ? (hourlyHeartbeatData[selectedHourIndex] || [0, 0]) : [0, 0];
    const selectedHour = selectedHourIndex >= 0 ? selectedHourIndex : 0;
    const selectedHourRange = selectedHourIndex >= 0 ? `${selectedHour.toString().padStart(2, '0')}:00 - ${selectedHour.toString().padStart(2, '0')}:59` : '';
    const selectedHeartbeatRange = selectedHourIndex >= 0 ? `${selectedHourData[0]} - ${selectedHourData[1]} bpm` : '';

    // 선택된 날짜의 가장 최근 기록 시간
    const latestRecordTime = useMemo(() => {
        if (!physicalRecords || physicalRecords.length === 0) return null;

        const selectedDateStr = selectedDateForHourly.toISOString().split('T')[0];
        const dayRecords = physicalRecords.filter(record =>
            record.date && record.date.startsWith(selectedDateStr) &&
            (record.minHeartRate != null || record.maxHeartRate != null || record.avgHeartRate != null)
        );

        if (dayRecords.length === 0) return null;

        // 가장 최근 기록 찾기 (date 기준으로 정렬)
        const sortedRecords = dayRecords.sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            return dateB.getTime() - dateA.getTime(); // 내림차순 정렬
        });

        const latestRecord = sortedRecords[0];
        if (!latestRecord || !latestRecord.date) return null;

        // 시간 포맷팅 (오전/오후 HH:MM 형식)
        return formatDateTime(latestRecord.date, '', ':', 11, 16, true);
    }, [physicalRecords, selectedDateForHourly]);

    // Y축 값 계산
    const calculateYAxisValues = () => {
        return {
            maxValue: 180,
            minValue: 40,
            yAxisValues: [40, 60, 80, 100, 120, 140, 160]
        };
    };

    // 주간 이동 함수들
    const handlePreviousWeek = () => {
        setCurrentWeekIndex(currentWeekIndex - 1);
        clearSelectedBar();
    };

    const handleNextWeek = () => {
        const today = new Date();
        const startOfWeek = new Date(today);
        const dayOfWeek = today.getDay();
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        startOfWeek.setDate(today.getDate() + mondayOffset);

        const nextWeekStart = new Date(startOfWeek);
        nextWeekStart.setDate(startOfWeek.getDate() + (currentWeekIndex + 1) * 7);

        if (nextWeekStart <= today) {
            setCurrentWeekIndex(currentWeekIndex + 1);
            clearSelectedBar();
        }
    };

    // 월간 이동 함수들
    const handlePreviousMonth = () => {
        setCurrentMonthIndex(currentMonthIndex - 1);
        clearSelectedBar();
    };

    const handleNextMonth = () => {
        const today = new Date();
        const nextMonthIndex = currentMonthIndex + 1;

        const targetMonth = new Date(today);
        targetMonth.setMonth(today.getMonth() + nextMonthIndex);
        const nextMonthFirstDay = new Date(targetMonth.getFullYear(), targetMonth.getMonth(), 1);

        if (nextMonthFirstDay <= today) {
            setCurrentMonthIndex(nextMonthIndex);
            clearSelectedBar();
        }
    };

    // 연간 이동 함수들
    const handlePreviousYear = () => {
        setCurrentYearIndex(currentYearIndex - 1);
        clearSelectedBar();
    };

    const handleNextYear = () => {
        const today = new Date();
        const nextYearIndex = currentYearIndex + 1;

        const targetYear = new Date(today);
        targetYear.setFullYear(today.getFullYear() + nextYearIndex);
        const nextYearFirstDay = new Date(targetYear.getFullYear(), 0, 1);

        if (nextYearFirstDay <= today) {
            setCurrentYearIndex(nextYearIndex);
            clearSelectedBar();
        }
    };

    const yAxisData = calculateYAxisValues();

    return (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* 상단 뷰 선택기 */}
            <Toggle
                options={[t('daily'), t('weekly'), t('monthly'), t('yearly')]}
                value={activeView}
                onChange={(newView) => {
                    setActiveView(newView);
                    clearSelectedBar();
                }}
            />

            {/* 뷰별 콘텐츠 렌더링 */}
            {activeView === 0 ? (
                // 일간 뷰: 기존 UI 유지
                <>
                    {/* 차트 - 일간일 때만 WeeklyChart 표시 */}
                    <WeeklyChart
                        data={allWeeklyData}
                        color={theme.palette.figma.heartRate}
                        height={50}
                        barWidth={10}
                        gap={28}
                        startFromEnd={true}
                        targetValue={CHALLENGE_HEARTBEAT_MAX}
                        yAxisLabels={['', '140', '', '40']}
                        isRangeData={true}
                        enableWeekSwipe={false}
                        selectedDate={selectedDate}
                        onScrollToEnd={handleScrollToEnd}
                        onDateSelect={(item) => {
                            // 날짜 문자열을 Date 객체로 변환하여 selectedDate 업데이트
                            if (item && item.day) {
                                const selectedDateObj = new Date(item.day);
                                setSelectedDate(selectedDateObj);
                            }
                        }}
                        sx={{
                            marginTop: '20px',
                            width: '360px',
                            alignSelf: 'center',
                        }}
                    />
                </>
            ) : activeView === 1 ? (
                // 주간 뷰: heartbeatWeek.js와 동일한 ReportCard 사용
                <CommonCard>
                    <ReportCard
                        activeView="week"
                        title={getWeeklyTitle}
                        mainValue={`${calculateCurrentWeekStats.minHeartRate} - ${calculateCurrentWeekStats.maxHeartRate}`}
                        mainUnit={"bpm"}
                        chartData={getCurrentWeekData}
                        yAxisData={yAxisData}
                        monthDays={null}
                        selectedBarIndex={selectedBarIndex}
                        onBarSelect={handleBarSelect}
                        onPreviousWeek={handlePreviousWeek}
                        onNextWeek={handleNextWeek}
                        onPreviousMonth={null}
                        onNextMonth={null}
                        onPreviousYear={null}
                        onNextYear={null}
                        isRangeData={true}
                    />
                </CommonCard>
            ) : activeView === 2 ? (
                // 월간 뷰: heartbeatMonth.js와 동일한 ReportCard 사용
                <CommonCard>
                    <ReportCard
                        activeView="month"
                        title={getMonthlyTitle}
                        mainValue={`${calculateCurrentMonthStats.minHeartRate} - ${calculateCurrentMonthStats.maxHeartRate}`}
                        mainUnit={"bpm"}
                        chartData={getCurrentMonthData}
                        yAxisData={yAxisData}
                        monthDays={getCurrentMonthDays}
                        selectedBarIndex={selectedBarIndex}
                        onBarSelect={handleBarSelect}
                        onPreviousWeek={null}
                        onNextWeek={null}
                        onPreviousMonth={handlePreviousMonth}
                        onNextMonth={handleNextMonth}
                        onPreviousYear={null}
                        onNextYear={null}
                        isRangeData={true}
                    />
                </CommonCard>
            ) : (
                // 연간 뷰: heartbeatYear.js와 동일한 ReportCard 사용
                <CommonCard>
                    <ReportCard
                        activeView="year"
                        title={getYearlyTitle}
                        mainValue={`${calculateCurrentYearStats.minHeartRate} - ${calculateCurrentYearStats.maxHeartRate}`}
                        mainUnit={"bpm"}
                        chartData={getCurrentYearData}
                        yAxisData={yAxisData}
                        monthDays={null}
                        selectedBarIndex={selectedBarIndex}
                        onBarSelect={handleBarSelect}
                        onPreviousWeek={null}
                        onNextWeek={null}
                        onPreviousMonth={null}
                        onNextMonth={null}
                        onPreviousYear={handlePreviousYear}
                        onNextYear={handleNextYear}
                        isRangeData={true}
                    />
                </CommonCard>
            )}

            {/* 메인 콘텐츠 - 일간 뷰일 때만 표시 */}
            {activeView === 0 && (
                <Box sx={{ display: 'flex', gap: 2 }}>
                    {/* 좌측 메인 영역 */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* 심박수 요약 카드 */}
                        <CommonCard>
                            {/* 메인 심박수 범위 표시 */}
                            <Box sx={{
                                display: 'flex',
                                gap: '5px',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative',
                                mb: 2
                            }}>
                                <Typography sx={{
                                    color: theme.palette.figma.white[80],
                                    fontSize: '40px',
                                    fontWeight: 600,
                                    letterSpacing: '-1.2px',
                                    lineHeight: 1,
                                    whiteSpace: 'nowrap'
                                }}>
                                    {activeView === 0 || activeView === 1
                                        ? (currentDayData ? `${currentDayData.min} - ${currentDayData.max}` : '-')
                                        : `${currentViewStats.min} - ${currentViewStats.max}`
                                    }
                                </Typography>
                                <Typography sx={{
                                    position: 'absolute',
                                    right: '-5px',
                                    top: '50%',
                                    transform: 'translate(100%, -50%)',
                                    color: theme.palette.figma.white[60],
                                    fontSize: '14px',
                                    fontWeight: 400,
                                    letterSpacing: '-0.42px',
                                    lineHeight: 1,
                                    whiteSpace: 'nowrap'
                                }}>
                                    bpm
                                </Typography>
                            </Box>

                            {/* 진행률 바 섹션 */}
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '10px',
                                alignItems: 'center',
                                width: '100%',
                                mb: 2
                            }}>
                                {(activeView === 0 || activeView === 1 ? currentDayData : currentViewStats.min > 0 || currentViewStats.max > 0) ? (
                                    <ProgressBar
                                        rangeStart={40}
                                        rangeEnd={120}
                                        start={activeView === 0 || activeView === 1 ? (currentDayData?.min || 0) : currentViewStats.min}
                                        end={activeView === 0 || activeView === 1 ? (currentDayData?.max || 0) : currentViewStats.max}
                                        height="30px"
                                        showCurrentIndicator={false}
                                        showProgress={true}
                                        showStartEndValues={true}
                                        backgroundColor={theme.palette.figma.white[20]}
                                        progressColor={theme.palette.figma.heartRate}
                                        borderRadius="20px"
                                        sx={{ width: '300px' }}
                                    />
                                ) : (
                                    <Typography sx={{
                                        color: theme.palette.figma.white[60],
                                        fontSize: '14px',
                                    }}>
                                        데이터 없음
                                    </Typography>
                                )}
                            </Box>

                            {/* 최근 기록 */}
                            <Box sx={{
                                display: 'flex',
                                gap: '5px',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <Typography sx={{
                                    color: theme.palette.figma.heartRate,
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    letterSpacing: '-0.42px',
                                    lineHeight: 1,
                                    whiteSpace: 'nowrap'
                                }}>
                                    {activeView === 0 || activeView === 1
                                        ? (currentDayData?.avg ?? '-')
                                        : (currentViewStats.avg > 0 ? currentViewStats.avg : '-')
                                    }
                                </Typography>
                                {(activeView === 0 || activeView === 1) && (
                                    <Typography sx={{
                                        color: theme.palette.figma.white[60],
                                        fontSize: '12px',
                                        fontWeight: 400,
                                        letterSpacing: '-0.36px',
                                        lineHeight: 1,
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {latestRecordTime ? `${t('recent_record')} ${latestRecordTime}` : `${t('recent_record')} -`}
                                    </Typography>
                                )}
                            </Box>
                        </CommonCard>
                    </Box>

                    {/* 우측 그래프 영역  */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* 시간별 심박수 차트 */}
                        <CommonCard sx={{ alignItems: 'flex-start' }}>
                            <ChartSection
                                title={t('hourly_min_max_heart_rate')}
                                renderChart={
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '10px',
                                        width: '100%'
                                    }}>
                                        {/* 차트 영역 */}
                                        <Box sx={{
                                            display: 'flex',
                                            gap: '5px',
                                            alignItems: 'flex-start',
                                            width: '100%',
                                            position: 'relative'
                                        }}>
                                            {/* 메인 차트 영역 */}
                                            <Box sx={{
                                                flex: 1,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '3px',
                                                overflow: 'hidden'
                                            }}>
                                                {/* 선택된 시간대 팝업 */}
                                                {selectedHourIndex >= 0 && (
                                                    <Box sx={{
                                                        height: '27px',
                                                        position: 'absolute',
                                                        width: '100%'
                                                    }}>
                                                        <Box sx={{
                                                            position: 'absolute',
                                                            left: (() => {
                                                                let _index = selectedHourIndex;
                                                                if (selectedHourIndex <= 2) {
                                                                    _index = 3;
                                                                } else if (selectedHourIndex >= 19) {
                                                                    _index = 18
                                                                }
                                                                return `${_index * 4 + 2.5}%`;
                                                            })(),
                                                            top: 0,
                                                            display: 'flex',
                                                            gap: '5px',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            padding: '5px 10px',
                                                            backgroundColor: theme.palette.figma.heartRateDark,
                                                            borderRadius: '10px',
                                                            zIndex: 1,
                                                            transform: 'translateX(-50%)'
                                                        }}>
                                                            <Typography sx={{
                                                                color: theme.palette.figma.white[80],
                                                                fontSize: '12px',
                                                                fontWeight: 400,
                                                                letterSpacing: '-0.36px',
                                                                lineHeight: 1,
                                                                textAlign: 'right',
                                                                whiteSpace: 'nowrap',
                                                                zIndex: 3
                                                            }}>
                                                                {selectedHourRange}
                                                            </Typography>
                                                            <Typography sx={{
                                                                color: theme.palette.figma.heartRate,
                                                                fontSize: '14px',
                                                                fontWeight: 500,
                                                                letterSpacing: '-0.42px',
                                                                lineHeight: 1,
                                                                textAlign: 'right',
                                                                whiteSpace: 'nowrap',
                                                                zIndex: 2
                                                            }}>
                                                                {selectedHeartbeatRange}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                )}

                                                {/* 24시간 바 차트 */}
                                                <Box sx={{
                                                    height: '100px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    position: 'relative',
                                                    width: '100%'
                                                }}>
                                                    {/* Y축 그리드 라인 */}
                                                    <Box sx={{
                                                        position: 'absolute',
                                                        left: 0,
                                                        right: '20px',
                                                        top: '44px',
                                                        height: '1px',
                                                        backgroundColor: theme.palette.figma.white[30],
                                                        zIndex: 0
                                                    }} />

                                                    <Typography sx={{
                                                        position: 'absolute',
                                                        right: '0px',
                                                        top: '40px',
                                                        color: theme.palette.figma.white[60],
                                                        fontSize: '10px',
                                                        fontWeight: 500,
                                                        letterSpacing: '-0.3px',
                                                        lineHeight: 1,
                                                        whiteSpace: 'nowrap',
                                                        zIndex: 1
                                                    }}>
                                                        140
                                                    </Typography>

                                                    <Box sx={{
                                                        position: 'absolute',
                                                        left: 0,
                                                        right: '20px',
                                                        bottom: '0px',
                                                        height: '1px',
                                                        backgroundColor: theme.palette.figma.white[30],
                                                        zIndex: 0
                                                    }} />

                                                    <Typography sx={{
                                                        position: 'absolute',
                                                        right: '0px',
                                                        bottom: '-4px',
                                                        color: theme.palette.figma.white[60],
                                                        fontSize: '10px',
                                                        fontWeight: 400,
                                                        letterSpacing: '-0.3px',
                                                        lineHeight: 1,
                                                        whiteSpace: 'nowrap',
                                                        zIndex: 1
                                                    }}>
                                                        40
                                                    </Typography>

                                                    {/* 24개 바 생성 */}
                                                    {hourlyHeartbeatData.map((heartbeatRange, index) => {
                                                        const [min, max] = heartbeatRange;

                                                        // Y축 범위 40-CHALLENGE_HEARTBEAT_MAX 차트 전체 높이(100px)에 매핑
                                                        const yAxisMin = 40;
                                                        const yAxisMax = CHALLENGE_HEARTBEAT_MAX;
                                                        const yAxisRange = yAxisMax - yAxisMin;

                                                        const maxPosition = ((yAxisMax - max) / yAxisRange) * 100;
                                                        const minPosition = ((yAxisMax - min) / yAxisRange) * 100;

                                                        const barTop = maxPosition;
                                                        const barHeight = minPosition - maxPosition;

                                                        const barWidth = 3.4;
                                                        const gap = 0.5;
                                                        const barLeft = index * (barWidth + gap);

                                                        return (
                                                            <Box
                                                                key={index}
                                                                sx={{
                                                                    height: '100px',
                                                                    width: `${barWidth}%`,
                                                                    position: 'absolute',
                                                                    left: `${barLeft}%`,
                                                                    zIndex: 25,
                                                                    cursor: 'pointer'
                                                                }}
                                                                onClick={() => setSelectedHourIndex(index)}
                                                            >
                                                                <Box sx={{
                                                                    display: selectedHourIndex === index ? 'flex' : 'none',
                                                                    position: 'absolute',
                                                                    left: `50%`,
                                                                    top: '24px',
                                                                    width: '1px',
                                                                    height: `${barTop - 24}px`,
                                                                    backgroundColor: theme.palette.figma.heartRateDark,
                                                                }} />
                                                                <Box sx={{
                                                                    position: 'absolute',
                                                                    left: 0,
                                                                    top: `${barTop}px`,
                                                                    width: '100%',
                                                                    height: `${barHeight}px`,
                                                                    backgroundColor: theme.palette.figma.heartRate,
                                                                    borderRadius: '4px',
                                                                }} />
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>

                                                {/* X축 시간 표시 */}
                                                <Box sx={{
                                                    height: '4px',
                                                    display: 'flex',
                                                    alignItems: 'flex-end',
                                                    justifyContent: 'space-between',
                                                    width: 'calc(100% - 20px)'
                                                }}>
                                                    {[0, 1, 2, 3].map((_, index) => (
                                                        <Box key={index} sx={{ flex: 1, display: 'flex', gap: '10px', alignItems: 'center' }}>
                                                            <Box sx={{
                                                                height: '4px',
                                                                width: '1px',
                                                                backgroundColor: theme.palette.figma.white[30],
                                                                borderRadius: '2px'
                                                            }} />
                                                        </Box>
                                                    ))}
                                                </Box>

                                                {/* X축 라벨 */}
                                                <Box sx={{
                                                    height: '14px',
                                                    display: 'flex',
                                                    alignItems: 'flex-end',
                                                    justifyContent: 'space-between',
                                                    width: 'calc(100% - 20px)'
                                                }}>
                                                    {['00:00', '06:00', '12:00', '18:00'].map((time, index) => (
                                                        <Box key={index} sx={{ flex: 1 }}>
                                                            <Typography sx={{
                                                                color: theme.palette.figma.white[60],
                                                                fontSize: '12px',
                                                                fontWeight: 400,
                                                                letterSpacing: '-0.36px',
                                                                lineHeight: 1,
                                                                textAlign: 'left'
                                                            }}>
                                                                {time}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>
                                }
                            />
                        </CommonCard>
                    </Box>
                </Box>
            )}
        </Box>
    );
};
